class CollectionFilter {
  slugifyForId(str) {
    return String(str || "")
      .toLowerCase()
      .trim()
      .replace(/[\s\/\\]+/g, "-")
      .replace(/[^a-z0-9\-_]/g, "");
  }

  escapeHtmlAttr(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  constructor(collectionHandle, loadingSpinnerHTML, collectionTitle) {
    this.input = document.getElementById("customCollectionSearch");
    this.productGrid = document.getElementById("product-grid");
    this.sortCheckbox = document.getElementById("custom-sort");
    this.typesFilter = document.getElementById("typesFilter");
    this.spaceFilter = document.getElementById("spaceFilter");
    this.brandsFilter = document.getElementById("brandsFilter");
    this.openFilterButtons = document.getElementById("customSearchButton");
    this.specialFilters = document.querySelector("special-filters");
    this.clearFiltersButton = document.getElementById("clearFiltersButton");
    this.paginationContainer = document.querySelector(".pagination-wrapper");
    this.paginationContainerTop = document.querySelector(
      ".pagination-wrapper-top"
    );

    this.collectionHandle = collectionHandle;
    this.collectionTitle = collectionTitle;
    this.loadingSpinnerHTML = loadingSpinnerHTML;
    this.loadingContainer = null;
    this.products = [];
    this.filteredProducts = [];
    this.renderedProductElements = [];
    this.urlParams = new URLSearchParams(window.location.search);
    this.debounceTimer = null;

    this.productsPerPage = 30;
    this.currentPage = 1;
    this.paginationData = null;
    this.isInitialLoad = true;

    this.init();
  }

  async init() {
    this.setupLoadingSpinner();
    await this.fetchAllProducts();
    this.renderAllProducts();
    this.setupUrlParams();
    this.setupFilters();
    this.bindEvents();
    this.applyInitialFilters();
    this.setupProductsPerPage();
  }

  setupProductsPerPage() {
    document.querySelectorAll(".grid-sorting").forEach((sorting) => {
      sorting.addEventListener("click", () => {
        this.productsPerPage = parseInt(sorting.value) * 6;
        this.updateProductVisibility();
      });
    });
  }

  setupLoadingSpinner() {
    this.loadingContainer = document.createElement("div");
    this.loadingContainer.id = "filter-loading-container";
    this.loadingContainer.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.8);
        display: none;
        justify-content: center;
        align-items: center;
        z-index: 10;
      `;
    this.loadingContainer.innerHTML = this.loadingSpinnerHTML;
    this.productGrid.parentElement.appendChild(this.loadingContainer);
  }

  getLRUIndex(storage) {
    try { return JSON.parse(storage.getItem("cf_lru") || "{}"); } catch (_) { return {}; }
  }

  updateLRU(storage) {
    try {
      const lru = this.getLRUIndex(storage);
      lru[this.collectionHandle] = Date.now();
      storage.setItem("cf_lru", JSON.stringify(lru));
    } catch (_) {}
  }

  evictLRU(storage) {
    const lru = this.getLRUIndex(storage);
    const oldest = Object.entries(lru).sort(([, a], [, b]) => a - b)[0];
    if (!oldest) return false;
    const [handle] = oldest;
    storage.removeItem(`cf_products_${handle}`);
    delete lru[handle];
    try { storage.setItem("cf_lru", JSON.stringify(lru)); } catch (_) {}
    return true;
  }

  getCachedProducts() {
    const cacheKey = `cf_products_${this.collectionHandle}`;
    const ttl = 5 * 60 * 1000;

    for (const storage of [sessionStorage, localStorage]) {
      try {
        const raw = storage.getItem(cacheKey);
        if (!raw) continue;
        const { products, timestamp } = JSON.parse(raw);
        if (Date.now() - timestamp < ttl) {
          this.updateLRU(storage);
          return products;
        }
        storage.removeItem(cacheKey);
      } catch (_) {}
    }
    return null;
  }

  setCachedProducts(products) {
    const cacheKey = `cf_products_${this.collectionHandle}`;
    const slim = products.map(({ handle, title, type, vendor, url, featured_image, images, tags, metafields }) => ({
      handle,
      title,
      type,
      vendor,
      url,
      featured_image,
      images: images ? [images[0], images[1]].filter(Boolean) : [],
      tags,
      metafields: metafields?.spaces ? { spaces: metafields.spaces } : {},
    }));
    const payload = JSON.stringify({ products: slim, timestamp: Date.now() });

    const trySet = (storage) => {
      for (let attempts = 0; attempts < 10; attempts++) {
        try {
          storage.setItem(cacheKey, payload);
          this.updateLRU(storage);
          return;
        } catch (e) {
          if (e.name === "QuotaExceededError" || e.code === 22) {
            if (!this.evictLRU(storage)) return;
          } else {
            return;
          }
        }
      }
    };

    trySet(sessionStorage);
    trySet(localStorage);
  }

  async fetchAllProducts() {
    const cached = this.getCachedProducts();
    if (cached) {
      this.products = cached;
      return;
    }

    try {
      let allProducts = [];
      let totalPages = 1;

      const firstResponse = await fetch(
        `/collections/${this.collectionHandle}?view=json&page=1`
      );
      const firstData = await firstResponse.json();

      allProducts = firstData.products || [];
      totalPages = firstData.pagination?.total_pages || 1;

      this.paginationData = firstData.pagination;

      if (totalPages > 1) {
        const pagePromises = [];
        for (let page = 2; page <= totalPages; page++) {
          pagePromises.push(
            fetch(
              `/collections/${this.collectionHandle}?view=json&page=${page}`
            )
              .then((res) => res.json())
              .then((data) => data.products || [])
          );
        }

        const remainingPages = await Promise.all(pagePromises);
        remainingPages.forEach((products) => {
          allProducts = allProducts.concat(products);
        });
      }

      const cleanedProducts = allProducts.map((product) => {
        if (product.vendor) {
          product.vendor = this.decodeHtmlEntities(product.vendor);
        }

        if (product.title) {
          product.title = this.decodeHtmlEntities(product.title);
        }

        return product;
      });

      this.products = cleanedProducts;
      this.setCachedProducts(cleanedProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
      this.products = [];
    }
  }

  groupProductsByNumberPrefix() {
    const groupedByPrefix = {};

    this.products.forEach((product) => {
      const prefix = product.title.split(" - ")[0];

      if (!groupedByPrefix[prefix]) {
        groupedByPrefix[prefix] = [];
      }

      groupedByPrefix[prefix].push(product);
    });

    return groupedByPrefix;
  }

  renderAllProducts() {
    this.productGrid.innerHTML = "";
    const initialLoadingOverlay =
      this.productGrid.parentElement?.querySelector(".loading-overlay");
    if (initialLoadingOverlay) {
      initialLoadingOverlay.remove();
    }

    const groupMap = new Map();
    this.products.forEach((product) => {
      const rawPrefix = product.title.split(" - ")[0];
      const normalizedPrefix = rawPrefix.trim().toLowerCase();
      if (!groupMap.has(normalizedPrefix)) {
        groupMap.set(normalizedPrefix, { rawPrefix: rawPrefix.trim(), products: [] });
      }
      groupMap.get(normalizedPrefix).products.push(product);
    });

    let uniqueIndex = 0;
    groupMap.forEach(({ rawPrefix, products }) => {
      const parent = products.find((p) => {
        const tags = Array.isArray(p.tags)
          ? p.tags
          : String(p.tags || "").split(",").map((t) => t.trim());
        return tags.includes("collection-parent");
      }) || products[0];

      const productElement = this.createProductElement(parent, uniqueIndex, rawPrefix);
      this.productGrid.appendChild(productElement);
      this.renderedProductElements.push(productElement);
      uniqueIndex++;
    });
  }

  createProductElement(product, index, displayTitle = null) {
    function escapeHtml(str) {
      return String(str || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }
    
    const shortTitle = displayTitle || product.title.split(" - ")[0].trim();
    
    const li = document.createElement("li");
    li.className = "grid__item product-item";
    li.dataset.handle = product.handle;
    li.dataset.title = shortTitle.toLowerCase();
    li.dataset.type = product.type || "";
    li.dataset.vendor = product.vendor || "";
    li.dataset.index = index.toString();
    li.dataset.sortTitle = shortTitle.toLowerCase();
    li.style.order = index.toString();

    const spaces = product.metafields?.spaces || [];
    const spaceEntries = Array.isArray(spaces)
      ? spaces.map((s) => s.space_entry).join(",")
      : "";
    li.dataset.spaces = spaceEntries;

    const productUrl = product.url || `/products/${product.handle}`;
    const imageUrl = product.featured_image || "";
    const secondImage = product.images[1] || "";

    if (secondImage) {
      const preloadImg = new Image();
      preloadImg.src = secondImage;
    }

    const hoverAttributes = secondImage
      ? `onmouseenter="this.src='${secondImage}'" onmouseleave="this.src='${imageUrl}'"`
      : '';

    li.innerHTML = `
      <a href="${productUrl}" class="custom-product-link">
        <img
          src="${imageUrl}"
          class="custom-product-image"
          width="100%"
          height="auto"
          loading="lazy"
          alt="${escapeHtml(shortTitle)}"
          ${hoverAttributes}
        >
        <p class="custom-product-title">${escapeHtml(shortTitle)}</p>
        <p class="custom-product-collection">${product.vendor}</p>
      </a>
    `;

    return li;
  }

  setupUrlParams() {
    this.term = this.urlParams.get("search") || "";
    this.sorted = this.urlParams.get("sorting") === "a-to-z";
    this.currentPage = parseInt(this.urlParams.get("page")) || 1;

    this.urlTypes = this.urlParams.get("types")
      ? this.urlParams
          .get("types")
          .split(",")
          .map((v) => decodeURIComponent(v).toLowerCase().trim())
          .filter((t) => t)
      : [];

    this.urlSpaces = this.urlParams.get("spaces")
      ? this.urlParams
          .get("spaces")
          .split(",")
          .map((v) => decodeURIComponent(v).toLowerCase().trim())
          .filter((s) => s)
      : [];

    this.urlBrands = this.urlParams.get("brands")
      ? this.urlParams
          .get("brands")
          .split(",")
          .map((v) => decodeURIComponent(v).toLowerCase().trim())
          .filter((b) => b)
      : [];
  }

  setupFilters() {
    const uniqueTypes = [
      ...new Set(
        this.products
          .map((product) => product.type)
          .filter((type) => type !== "" && type != null)
      ),
    ];

    const uniqueSpaceEntries = [
      ...new Set(
        this.products
          .flatMap((product) => {
            const spaces = product.metafields?.spaces;
            return Array.isArray(spaces)
              ? spaces.map((item) => item.space_entry)
              : [];
          })
          .filter((s) => s != null && s !== "")
      ),
    ];

    const brands = [
      ...new Set(
        this.products
          .map((product) => product.vendor)
          .filter((vendor) => vendor !== "" && vendor != null)
      ),
    ];

    this.renderFilteredTags(
      uniqueTypes,
      this.typesFilter,
      this.urlTypes,
      "types"
    );
    this.renderFilteredTags(
      uniqueSpaceEntries,
      this.spaceFilter,
      this.urlSpaces,
      "spaces"
    );
    this.renderFilteredTags(
      brands,
      this.brandsFilter,
      this.urlBrands,
      "brands"
    );
  }

  decodeHtmlEntities(str) {
    const parser = new DOMParser();
    const decodedStr = parser.parseFromString(str, "text/html").documentElement
      .textContent;

    return decodedStr;
  }

  slugifyForId(str) {
    return String(str || "")
      .toLowerCase()
      .trim()
      .replace(/[\s\/\\]+/g, "-")
      .replace(/[^a-z0-9\-_]/g, "");
  }

  escapeHtmlAttr(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  renderFilteredTags(
    items,
    elementToAppend,
    selectedItems = [],
    groupName = "generic"
  ) {
    if (!elementToAppend) return;

    elementToAppend.innerHTML = items
      .map((item) => {
        const safeId = `${groupName}-${this.slugifyForId(item)}`;
        const checked = selectedItems.includes(item) ? "checked" : "";
        const safeValue = this.escapeHtmlAttr(item);

        return `
        <div class="custom-filter-item">
          <input
            class="custom-checkbox"
            type="checkbox"
            name="${groupName}"
            value="${safeValue}"
            id="${safeId}"
            data-group="${groupName}"
            ${checked}
          >
          <label class="custom-checkbox-label" for="${safeId}">
            <span class="custom-checkbox-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24">
                  <path fill="none" stroke="#f89d3c" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5v14a2 2 0 0 0 2.75 1.84L20 13.74a2 2 0 0 0 0-3.5L7.75 3.14A2 2 0 0 0 5 4.89"/>
              </svg>
            </span>
            <span>${this.escapeHtmlAttr(item)}</span>
          </label>
        </div>
      `;
      })
      .join("");
  }

  renderSelectedFilters(selectedFilters) {
    const filters = document.getElementById("selectedFilters");
    if (!filters) return;

    filters.innerHTML = selectedFilters
      .map(
        (filter) => `
      <button class="selected-filter-button" data-filter="${this.escapeHtmlAttr(
        filter
      )}">
      ${this.escapeHtmlAttr(filter)}
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
        <path fill="#6b6b6b" d="m7.05 5.636 4.95 4.95 4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.415 1.414-4.95-4.95-4.949 4.95-1.414-1.414 4.95-4.95-4.95-4.95z"></path>
      </svg>
      </button>
    `
      )
      .join("");

    const buttons = document.querySelectorAll(".selected-filter-button");

    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        const filterValue = button.dataset.filter;

        const checkbox = document.querySelector(
          `input[type="checkbox"][value="${filterValue}"]`
        );

        if (checkbox) {
          checkbox.checked = false;
        }

        this.updateProductVisibility();
      });
    });
  }

  bindEvents() {
    this.openFilterButtons.addEventListener("click", (event) => {
      event.stopPropagation();
      this.toggleFilters();
    });

    this.input.addEventListener("input", () => this.handleInputChange());
    this.sortCheckbox.addEventListener("change", () =>
      this.updateProductVisibility()
    );
    if (this.typesFilter) {
      this.typesFilter.addEventListener("change", () =>
        this.updateProductVisibility()
      );
    }
    if (this.spaceFilter) {
      this.spaceFilter.addEventListener("change", () =>
        this.updateProductVisibility()
      );
    }
    if (this.brandsFilter) {
      this.brandsFilter.addEventListener("change", () =>
        this.updateProductVisibility()
      );
    }
    this.clearFiltersButton.addEventListener("click", () =>
      this.clearFilters()
    );

    window.addEventListener("click", (event) => {
      const filters = this.specialFilters;

      if (filters.hasAttribute("open") && !filters.contains(event.target)) {
        this.toggleFilters();
      }
    });
  }

  toggleFilters() {
    if (this.specialFilters.hasAttribute("open")) {
      this.specialFilters.removeAttribute("open");
      this.openFilterButtons.style.transform = "rotate(180deg)";
    } else {
      this.specialFilters.setAttribute("open", "");
      this.openFilterButtons.style.transform = "rotate(0deg)";
    }
  }

  handleInputChange() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.updateProductVisibility();
    }, 200);
  }

  showLoading() {
    this.loadingContainer.style.display = "flex";
  }

  hideLoading() {
    this.loadingContainer.style.display = "none";
  }

  applySorting(isAtoZ) {
    if (isAtoZ) {
      this.filteredProducts.sort((a, b) => {
        const titleA = a.dataset.sortTitle;
        const titleB = b.dataset.sortTitle;
        return titleA.localeCompare(titleB);
      });
    } else {
      this.filteredProducts.sort((a, b) => {
        return parseInt(a.dataset.index) - parseInt(b.dataset.index);
      });
    }

    this.filteredProducts.forEach((item, index) => {
      item.style.order = index.toString();
    });
  }

  getCheckedValues(container) {
    if (!container) return [];
    return Array.from(
      container.querySelectorAll('input[type="checkbox"]:checked')
    ).map((cb) => cb.value);
  }

  updateProductVisibility() {
    this.showLoading();

    const searchTerm = this.input.value.trim().toLowerCase();
    const selectedTypes = this.getCheckedValues(this.typesFilter);
    const selectedSpaces = this.getCheckedValues(this.spaceFilter);
    const selectedBrands = this.getCheckedValues(this.brandsFilter);
    const isAtoZ = this.sortCheckbox.checked;

    const allSelectedFilters = [
      ...selectedTypes,
      ...selectedSpaces,
      ...selectedBrands,
    ];
    this.renderSelectedFilters(allSelectedFilters);

    const productItems = this.productGrid.querySelectorAll(".product-item");
    let visibleCount = 0;
    this.filteredProducts = [];

    productItems.forEach((item) => {
      const title = item.dataset.title;
      const handle = item.dataset.handle;
      const type = item.dataset.type;
      const vendor = item.dataset.vendor;
      const spaces = item.dataset.spaces ? item.dataset.spaces.split(",") : [];

      let shouldShow = true;

      if (
        searchTerm &&
        !title.includes(searchTerm) &&
        !handle.includes(searchTerm)
      ) {
        shouldShow = false;
      }

      if (selectedTypes.length > 0 && !selectedTypes.includes(type)) {
        shouldShow = false;
      }

      if (
        selectedSpaces.length > 0 &&
        !selectedSpaces.some((space) => spaces.includes(space))
      ) {
        shouldShow = false;
      }

      if (selectedBrands.length > 0 && !selectedBrands.includes(vendor)) {
        shouldShow = false;
      }

      if (shouldShow) {
        this.filteredProducts.push(item);
        visibleCount++;
      }
    });

    this.applySorting(isAtoZ);

    if (!this.isInitialLoad) {
      this.currentPage = 1;
    }
    this.isInitialLoad = false;
    
    this.renderPaginatedProducts();
    this.renderCustomPagination();

    this.handleNoResults(visibleCount);

    this.updateUrlParams(
      searchTerm,
      isAtoZ,
      selectedTypes,
      selectedSpaces,
      selectedBrands
    );

    setTimeout(() => {
      this.hideLoading();
    }, 100);
  }

  renderPaginatedProducts() {
    const productItems = this.productGrid.querySelectorAll(".product-item");

    productItems.forEach((item) => {
      item.style.display = "none";
    });

    const startIndex = (this.currentPage - 1) * this.productsPerPage;
    const endIndex = startIndex + this.productsPerPage;

    this.filteredProducts.slice(startIndex, endIndex).forEach((item) => {
      item.style.display = "block";
    });
  }

  generatePaginationHTML() {
    const totalPages = Math.ceil(
      this.filteredProducts.length / this.productsPerPage
    );

    let paginationHTML =
      '<nav class="pagination" role="navigation" aria-label="Pagination"><ul class="pagination__list list-unstyled" role="list">';

    if (this.currentPage > 1) {
      paginationHTML += `
        <li>
          <button class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" 
                  data-page="${this.currentPage - 1}" 
                  aria-label="Previous">
            <span class="svg-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24">
                <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
            </span>
          </button>
        </li>
      `;
    }

    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      this.currentPage - Math.floor(maxVisiblePages / 2)
    );
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      paginationHTML += `
        <li>
          <button class="pagination__item link" data-page="1" aria-label="Page 1">1</button>
        </li>
      `;
      if (startPage > 2) {
        paginationHTML += '<li><span class="pagination__item">...</span></li>';
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      if (i === this.currentPage) {
        paginationHTML += `
          <li>
            <button role="link" 
                    aria-disabled="true" 
                    class="pagination__item custom-pagination-selected" 
                    aria-current="page" 
                    aria-label="Page ${i}">
              ${i}
            </button>
          </li>
        `;
      } else {
        paginationHTML += `
          <li>
            <button class="pagination__item custom-pagination-link" 
                    data-page="${i}" 
                    aria-label="Page ${i}">
              ${i}
            </button>
          </li>
        `;
      }
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        paginationHTML += '<li><span class="pagination__item">...</span></li>';
      }
      paginationHTML += `
        <li>
          <button class="pagination__item custom-pagination-link" data-page="${totalPages}" aria-label="Page ${totalPages}">${totalPages}</button>
        </li>
      `;
    }

    if (this.currentPage < totalPages) {
      paginationHTML += `
        <li>
          <button class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" 
                  data-page="${this.currentPage + 1}" 
                  aria-label="Next">
            <span class="svg-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24">
                <path fill="currentColor" d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
              </svg>
            </span>
          </button>
        </li>
      `;
    }

    paginationHTML += "</ul></nav>";
    return paginationHTML;
  }

  bindPaginationEvents(container) {
    if (!container) return;
    const paginationButtons = container.querySelectorAll("button[data-page]");
    paginationButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        const page = parseInt(button.dataset.page);
        this.goToPage(page);
      });
    });
  }

  renderCustomPagination() {
    const totalPages = Math.ceil(
      this.filteredProducts.length / this.productsPerPage
    );

    if (totalPages <= 1) {
      if (this.paginationContainer) {
        this.paginationContainer.style.display = "none";
      }
      if (this.paginationContainerTop) {
        this.paginationContainerTop.style.display = "none";
      }
      return;
    }

    const paginationHTML = this.generatePaginationHTML();

    if (this.paginationContainer) {
      this.paginationContainer.style.display = "block";
      this.paginationContainer.innerHTML = paginationHTML;
      this.bindPaginationEvents(this.paginationContainer);
    }

    if (this.paginationContainerTop) {
      this.paginationContainerTop.style.display = "block";
      this.paginationContainerTop.innerHTML = paginationHTML;
      this.bindPaginationEvents(this.paginationContainerTop);
    }
  }

  goToPage(page) {
    this.currentPage = page;
    this.renderPaginatedProducts();
    this.renderCustomPagination();

    this.urlParams.set("page", this.currentPage.toString());
    history.replaceState({}, "", `?${this.urlParams.toString()}`);

    const productGridContainer = document.getElementById(
      "ProductGridContainer"
    );
    if (productGridContainer) {
      productGridContainer.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  handleNoResults(visibleCount) {
    let noResultsMsg = this.productGrid.querySelector(".no-results-message");

    if (visibleCount === 0) {
      if (!noResultsMsg) {
        noResultsMsg = document.createElement("p");
        noResultsMsg.className = "no-results-message";
        noResultsMsg.textContent = "No products found";
        this.productGrid.appendChild(noResultsMsg);
      }
      noResultsMsg.style.display = "block";
    } else if (noResultsMsg) {
      noResultsMsg.style.display = "none";
    }
  }

  updateUrlParams(
    searchTerm,
    isAtoZ,
    selectedTypes,
    selectedSpaces,
    selectedBrands
  ) {
    this.urlParams.set("search", searchTerm);
    this.urlParams.set("sorting", isAtoZ ? "a-to-z" : "default");
    this.urlParams.set(
      "types",
      selectedTypes.map(encodeURIComponent).join(",")
    );
    this.urlParams.set(
      "spaces",
      selectedSpaces.map(encodeURIComponent).join(",")
    );
    this.urlParams.set(
      "brands",
      selectedBrands.map(encodeURIComponent).join(",")
    );
    this.urlParams.set("page", this.currentPage.toString());
    history.replaceState({}, "", `?${this.urlParams.toString()}`);
  }

  applyInitialFilters() {
    this.input.value = this.term;
    this.sortCheckbox.checked = this.sorted;

    this.urlTypes.forEach((type) => {
      if (this.typesFilter) {
        const checkbox = Array.from(
          this.typesFilter.querySelectorAll('input[type="checkbox"]')
        ).find((cb) => cb.value === type);
        if (checkbox) checkbox.checked = true;
      }
    });

    this.urlSpaces.forEach((space) => {
      if (this.spaceFilter) {
        const checkbox = Array.from(
          this.spaceFilter.querySelectorAll('input[type="checkbox"]')
        ).find((cb) => cb.value === space);
        if (checkbox) checkbox.checked = true;
      }
    });

    this.urlBrands.forEach((brand) => {
      if (this.brandsFilter) {
        const checkbox = Array.from(
          this.brandsFilter.querySelectorAll('input[type="checkbox"]')
        ).find((cb) => cb.value === brand);
        if (checkbox) checkbox.checked = true;
      }
    });

    this.updateProductVisibility();
  }

  clearFilters() {
    this.input.value = "";
    this.sortCheckbox.checked = false;

    if (this.typesFilter) {
      const typeCheckboxes = this.typesFilter.querySelectorAll(
        'input[type="checkbox"]'
      );
      typeCheckboxes.forEach((checkbox) => (checkbox.checked = false));
    }

    if (this.spaceFilter) {
      const spaceCheckboxes = this.spaceFilter.querySelectorAll(
        'input[type="checkbox"]'
      );
      spaceCheckboxes.forEach((checkbox) => (checkbox.checked = false));
    }

    if (this.brandsFilter) {
      const brandCheckboxes = this.brandsFilter.querySelectorAll(
        'input[type="checkbox"]'
      );
      brandCheckboxes.forEach((checkbox) => (checkbox.checked = false));
    }

    this.urlParams.delete("search");
    this.urlParams.delete("sorting");
    this.urlParams.delete("types");
    this.urlParams.delete("spaces");
    this.urlParams.delete("brands");
    history.replaceState({}, "", `?${this.urlParams.toString()}`);

    this.updateProductVisibility();
  }
}

window.initCollectionFilter = function (
  collectionHandle,
  loadingSpinnerHTML,
  collectionTitle
) {
  new CollectionFilter(collectionHandle, loadingSpinnerHTML, collectionTitle);
};
